import { useAtomValue, useSetAtom } from "jotai";
import type { ChangeEvent } from "react";
import { useCallback, useEffect, useId, useMemo } from "react";
import {
  type IrrigationPlanHistoryFilters,
  type IrrigationPlanHistoryScope,
  irrigationPlanHistoryFiltersAtom,
  irrigationPlanHistoryPaginationAtom,
  irrigationPlanHistorySelectionReadyAtom,
  irrigationPlansByProjectIdAtom,
  projectsAtom,
  selectedPropertyAtom,
} from "@/store/data";
import { DateRangePresets } from "../components/DateRangePresets";

export function HistoryFiltersBar() {
  const projectSelectId = useId();
  const planSelectId = useId();
  const filters = useAtomValue(irrigationPlanHistoryFiltersAtom);
  const setFilters = useSetAtom(irrigationPlanHistoryFiltersAtom);
  const setPagination = useSetAtom(irrigationPlanHistoryPaginationAtom);
  const property = useAtomValue(selectedPropertyAtom);
  const projects = useAtomValue(projectsAtom);
  const irrigationPlansByProjectId = useAtomValue(irrigationPlansByProjectIdAtom);
  const selectionReady = useAtomValue(irrigationPlanHistorySelectionReadyAtom);

  const applyFilters = useCallback(
    (updater: (current: IrrigationPlanHistoryFilters) => IrrigationPlanHistoryFilters) => {
      let changed = false;
      setFilters((prev) => {
        const next = updater(prev);
        changed =
          prev.scope !== next.scope ||
          prev.projectId !== next.projectId ||
          prev.planId !== next.planId ||
          prev.startDate !== next.startDate ||
          prev.endDate !== next.endDate;
        return changed ? next : prev;
      });
      if (changed) {
        setPagination((prev) => (prev.offset === 0 ? prev : { ...prev, offset: 0 }));
      }
    },
    [setFilters, setPagination]
  );

  useEffect(() => {
    if (!projects.length) {
      if (filters.projectId !== null || filters.planId !== null) {
        applyFilters((prev) => ({ ...prev, projectId: null, planId: null }));
      }
      return;
    }

    let nextProjectId = filters.projectId;
    if (!nextProjectId || !projects.some((project) => project.id === nextProjectId)) {
      nextProjectId = projects[0].id;
    }

    let nextPlanId = filters.planId;
    if (filters.scope === "plan") {
      const plans = irrigationPlansByProjectId(nextProjectId);
      if (!plans.length) {
        nextPlanId = null;
      } else if (!nextPlanId || !plans.some((plan) => plan.id === nextPlanId)) {
        nextPlanId = plans[0].id;
      }
    } else {
      nextPlanId = null;
    }

    if (nextProjectId !== filters.projectId || nextPlanId !== filters.planId) {
      applyFilters((prev) => ({
        ...prev,
        projectId: nextProjectId,
        planId: nextPlanId,
      }));
    }
  }, [applyFilters, filters.planId, filters.projectId, filters.scope, irrigationPlansByProjectId, projects]);

  const plans = useMemo(() => {
    return filters.projectId ? irrigationPlansByProjectId(filters.projectId) : [];
  }, [filters.projectId, irrigationPlansByProjectId]);

  const handleScopeChange = useCallback(
    (scope: IrrigationPlanHistoryScope) => {
      if (scope === filters.scope) return;
      applyFilters((prev) => ({
        ...prev,
        scope,
        planId: scope === "project" ? null : prev.planId,
      }));
    },
    [applyFilters, filters.scope]
  );

  const handleProjectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const projectId = event.target.value || null;
      applyFilters((prev) => {
        const plansForProject = projectId ? irrigationPlansByProjectId(projectId) : [];
        const nextPlanId = prev.scope === "plan" ? (plansForProject[0]?.id ?? null) : null;
        return {
          ...prev,
          projectId,
          planId: nextPlanId,
        };
      });
    },
    [applyFilters, irrigationPlansByProjectId]
  );

  const handlePlanChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const planId = event.target.value || null;
      applyFilters((prev) => ({ ...prev, planId }));
    },
    [applyFilters]
  );

  const handlePresetChange = useCallback(
    (range: { start: string; end: string }) => {
      applyFilters((prev) => ({ ...prev, startDate: range.start, endDate: range.end }));
    },
    [applyFilters]
  );

  const disabled = !property;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Propriedade ativa</p>
          <p className="text-sm text-gray-900">{property?.name ?? "Nenhuma propriedade selecionada"}</p>
        </div>

        <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-100 p-1">
          {(
            [
              { id: "project", label: "Por projeto" },
              { id: "plan", label: "Por plano" },
            ] as Array<{ id: IrrigationPlanHistoryScope; label: string }>
          ).map((option) => {
            const active = filters.scope === option.id;
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => handleScopeChange(option.id)}
                className={`relative rounded-md px-3 py-1.5 text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  active ? "bg-white text-blue-600 shadow" : "text-gray-600 hover:text-gray-900"
                } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label htmlFor={projectSelectId} className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Projeto
          </label>
          <select
            id={projectSelectId}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={filters.projectId ?? ""}
            onChange={handleProjectChange}
            disabled={disabled || !projects.length}
          >
            {!projects.length ? <option value="">Nenhum projeto disponível</option> : null}
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {!projects.length ? (
            <p className="mt-1 text-xs text-gray-500">Nenhum projeto encontrado para a propriedade selecionada.</p>
          ) : null}
        </div>

        <div>
          <label htmlFor={planSelectId} className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Plano de irrigação
          </label>
          <select
            id={planSelectId}
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={filters.planId ?? ""}
            onChange={handlePlanChange}
            disabled={disabled || filters.scope !== "plan" || !projects.length || !plans.length}
          >
            {filters.scope !== "plan" ? <option value="">Selecione o modo "Por plano"</option> : null}
            {filters.scope === "plan" && !plans.length ? <option value="">Nenhum plano disponível</option> : null}
            {filters.scope === "plan"
              ? plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name}
                  </option>
                ))
              : null}
          </select>
        </div>
      </div>

      <DateRangePresets
        startDate={filters.startDate}
        endDate={filters.endDate}
        disabled={disabled}
        onApply={handlePresetChange}
      />

      {!selectionReady ? (
        <p className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-3 text-xs text-neutral-600">
          Defina o projeto {filters.scope === "plan" ? "e o plano " : ""}para carregar os dados do histórico.
        </p>
      ) : null}
    </div>
  );
}

export default HistoryFiltersBar;
